// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyBulkChange = { key: string; label: string; value: string };

export type TyBulkReviewDetails = {
    count: number;
    changes: TyBulkChange[];
    onConfirm: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * The last thing between a dropdown and eighty records.
 *
 * The bar says what is selected and holds the draft, but neither of those is the same as being told
 * what is about to happen — a person who has scrolled a row of twelve fields sideways has not seen
 * the three they filled in together, and the three together are the write. So it is read back as a
 * list: one line per field, the field's name and the value it will take, in words rather than in
 * the controls that produced them.
 *
 * It is a review, not a warning. Nothing is coloured as a danger, because setting a floor of flats
 * to the same type is ordinary work — it only has to be *seen* before it goes.
 */
export default function BulkReviewPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { count, changes, onConfirm } = details as TyBulkReviewDetails;

    const handleConfirm = () => {
        onClose();
        onConfirm();
    };

    return (
        <section className="popup-card bulk-review">
            <h2 className="bulk-review__title">{t.bulk.reviewTitle}</h2>
            <p className="bulk-review__hint">{t.bulk.reviewHint.replace("{count}", String(count))}</p>

            <span className="bulk-review__caption">{t.bulk.willChange}</span>

            <ul className="bulk-review__list">
                {changes.map((change) => (
                    <li className="bulk-review__row" key={change.key}>
                        <span className="bulk-review__label">{change.label}</span>
                        <span className="bulk-review__arrow" aria-hidden="true">
                            <Icon name="chevron" size={12} />
                        </span>
                        <span className="bulk-review__value">{change.value}</span>
                    </li>
                ))}
            </ul>

            <div className="bulk-review__actions">
                <button type="button" className="bulk-review__cancel" onClick={onClose}>
                    {t.common.cancel}
                </button>
                <button type="button" className="bulk-review__confirm" onClick={handleConfirm}>
                    {t.common.confirm}
                </button>
            </div>
        </section>
    );
}
